import { CoreApi, Product, ProductType } from '@inter-ikea-storageone/range';
import React from 'react';
import { getTranslatedTitle, getMPPImage, getDefaultHoverImage } from 'products';
import { useDrag } from 'react-dnd';
import { DraggableProduct } from 'products/interfaces/DraggableProduct';
import { extractImageSource } from 'utils/imageUtils';
import { ProductThumb, ThumbImage } from './components';
import { Behaviors, ProductCardImage } from 'rangeConfig/@types';
import styled from 'styled-components';
import { useIntl } from 'react-intl';
import KompisToolTip from 'ui/KompisToolTip/KompisToolTip';
import ProductImage from 'components/ProductImage/ProductImage';
import DraggableWrapper from 'components/ProductSidebar/DraggableWrapper/DraggableWrapper';
import { useWidget } from 'contexts/widget';
import { useCore } from 'contexts/core';

export const StyledButton = styled.button`
  position: relative;
  background: #fff;
  cursor: pointer;
  width: 65px;
  border: none;
  box-shadow: 0 0 0 rgba(0, 0, 0, 0), 0 0 0 rgba(0, 0, 0, 0);

  @media (hover: hover) {
    &:hover {
      box-shadow: 0 2px 2px rgba(0, 0, 0, 0.1), 0 8px 8px rgba(0, 0, 0, 0.04);
    }
  }
  &:focus {
    outline: 0.125rem solid ${({ theme }) => theme.colors.feedback.focus};
  }
  &[data-disabled='true'] {
    cursor: default;
    pointer-events: none;
    opacity: 0.5;
  }
`;

type Props = {
  product: Product;
  cluster?: Product[];
  onSelect: (product: Product) => void;
  selectedIds?: string[];
  title?: string;
  groupId: string;
  behaviours?: Behaviors;
  mainImage?: ProductCardImage;
  hoverImage?: ProductCardImage;
  selected?: (product: Product, coreApi: CoreApi) => boolean;
  toolTip?: string;
  disabled?: boolean;
  listIndex?: number;
};

function ProductThumbnail({
  product,
  cluster,
  onSelect,
  selectedIds,
  behaviours,
  selected,
  mainImage,
  hoverImage,
  toolTip,
  disabled,
  listIndex
}: Props) {
  const widgetContext = useWidget();
  const mainCardImage = (mainImage && mainImage(product)) || getMPPImage(product);
  const hoverCardImage = (hoverImage && hoverImage(product)) || getDefaultHoverImage(product);

  const { formatMessage } = useIntl();
  const coreApi = useCore();

  const tooltipText = toolTip
    ? getTranslatedTitle(toolTip, product, formatMessage)
    : getTranslatedTitle('name, typeName, designText', product, formatMessage);

  const item: DraggableProduct = {
    type: 'product',
    targets: [product],
    params: { ...widgetContext, meta: { listIndex } },
    behaviors: behaviours
  };

  const [{ isDragging }, drag] = useDrag({
    item,
    collect: monitor => ({
      isDragging: monitor.isDragging()
    })
  });

  const isFrame = (type: string) => {
    return type === ProductType.PAX_FRAME;
  };

  if (!mainCardImage) return null;
  const { srcset, fallbackImage } = extractImageSource(mainCardImage.sources);
  const checked = selected
    ? selected(product, coreApi)
    : selectedIds?.includes(product.coreProductId) ||
      !!cluster?.find(product => selectedIds?.includes(product.coreProductId));

  return (
    <ProductThumb
      data-testid={product.coreProductId}
      data-selected={checked}
      key={product.coreProductId}
      onClick={event => {
        disabled ? event.preventDefault() : onSelect(product);
      }}
      data-disabled={disabled}
      aria-disabled={disabled}
    >
      <KompisToolTip text={tooltipText} uniqueId={'color_' + product.coreProductId}>
        <StyledButton aria-describedby={'color_' + product.coreProductId}>
          {isFrame(product.type) ? (
            <ThumbImage src={fallbackImage.src} srcSet={srcset} alt={product.name}></ThumbImage>
          ) : (
            <DraggableWrapper data-is-dragging={isDragging} ref={drag}>
              <ProductImage
                sizes="9rem"
                image={mainCardImage}
                alt={product.name}
                hoverImage={hoverCardImage}
                hoverContainerClass={ThumbImage}
              />
            </DraggableWrapper>
          )}
        </StyledButton>
      </KompisToolTip>
    </ProductThumb>
  );
}

export default ProductThumbnail;
